import axios from "axios";
import { BASE_NODE_URL, NEW_PURCHASE_ORDER_PRODUCT_BY_UPC, NEW_PURCHASE_ORDER_PRODUCT_LIST } from "../../Constants/Config";
import { PERMISSIONS } from "../../Constants/permission";

/**
 * Container deposit (CRV) — the only place this feature talks to the server.
 * Every call goes to the NODE backend. Endpoints stay local to this folder so the
 * feature's footprint in shared files is just its route + menu entry.
 */

export const DEPOSIT_API = {
  SETTING: "deposit/setting",
  SETTING_UPDATE: "deposit/setting/update",
  RATES: "deposit/rates",
  RATES_SAVE: "deposit/rates/save",
  RATES_RESET: "deposit/rates/reset",
  PRODUCTS_CONFIGS: "deposit/products/configs",
  PRODUCTS_SAVE: "deposit/products/save",
  PRODUCTS_REMOVE: "deposit/products/remove",
  COPY: "deposit/copy",
  COPY_JOBS: "deposit/copy/jobs",
};

export class DepositApiError extends Error {}

export const isAbortError = (err) =>
  err?.code === "ERR_CANCELED" || err?.name === "CanceledError" || err?.name === "AbortError";

export async function depositPost(endpoint, auth, payload = {}, signal) {
  if (!auth?.token) throw new DepositApiError("Not authenticated");
  const { merchant_id, token_id, login_type, session_id } = auth;
  const response = await axios.post(
    `${BASE_NODE_URL}${endpoint}`,
    { merchant_id: String(merchant_id ?? ""), token_id, login_type: login_type ?? "merchant", session_id, ...payload },
    {
      headers: { Authorization: `Bearer ${auth.token}`, "Content-Type": "application/json", Accept: "application/json" },
      signal,
    },
  );
  const data = response?.data;
  if (data?.status === false) {
    const fieldError = data?.errors ? Object.values(data.errors)[0]?.msg : null;
    throw new DepositApiError(data?.message || fieldError || "Something went wrong.");
  }
  return data ?? {};
}

/**
 * Product search = the SHARED new_purchase_orders_product_list_v2 (PO, stocktake,
 * variants, composite use it too). Pages by PRODUCT; variants come nested.
 */
export async function searchCatalog(auth, { search, page = 1, perPage = 20 }, signal) {
  const data = await depositPost(
    NEW_PURCHASE_ORDER_PRODUCT_LIST,
    auth,
    { ...(search ? { search_by_product: search } : {}), page, per_page: perPage },
    signal,
  );
  const groups = (Array.isArray(data?.data) ? data.data : []).map((g) => ({
    id: Number(g.product_id),
    title: g.product_title || g.product_name,
    upc: g.upc || "",
    variants:
      String(g.is_variant) === "1"
        ? (g.variants || []).map((v) => ({
            id: Number(v.varient_id),
            title: v.variant_name || v.product_name,
            upc: v.upc || "",
          }))
        : [],
  }));
  return { groups, total: Number(data?.pagination?.total) || 0 };
}

/**
 * Exact barcode lookup = the SHARED new_purchase_order_product_by_upc (product UPC, variant UPC,
 * then custom codes). It answers a miss with status:false, which means "not found", not an error.
 */
export async function scanUpc(auth, upc, signal) {
  if (!auth?.token) throw new DepositApiError("Not authenticated");
  try {
    const data = await depositPost(NEW_PURCHASE_ORDER_PRODUCT_BY_UPC, auth, { upc }, signal);
    return data?.data || null;
  } catch (err) {
    if (err instanceof DepositApiError) return null;
    throw err;
  }
}

/**
 * While true, the menu item and page show for superadmin sessions only. UI only —
 * the Node API already scopes every deposit call to the caller's own stores.
 */
// Flipped to false 2026-10-21: open to every store. Set true to return to superadmin only.
export const DEPOSIT_SUPERADMIN_ONLY = false;

const OWNER_LOGIN_TYPES = ["merchant", "admin", "superadmin"];

/** Menu item and page: owners always; an employee with "Container Deposit" (CNTDT). Same rule as the sidebar entry. */
export function canUseContainerDeposit({ LoginGetDashBoardRecordJson, userTypeData } = {}, permissions = []) {
  const loginType = String(LoginGetDashBoardRecordJson?.login_type || userTypeData?.login_type || "");
  if (DEPOSIT_SUPERADMIN_ONLY) return loginType === "superadmin";
  return (
    OWNER_LOGIN_TYPES.includes(loginType) ||
    (Array.isArray(permissions) && permissions.includes(PERMISSIONS.MENU_CONTAINER_DEPOSIT))
  );
}

export const errorText = (err) =>
  err instanceof DepositApiError ? err.message : err?.response?.data?.message || "Something went wrong. Please try again.";

export const money = (n) =>
  `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const cents = (n) => {
  const v = Number(n) || 0;
  return v < 1 ? `${+(v * 100).toFixed(2)}¢` : money(v);
};

export const SIZE_UNITS = { oz: 1, ml: 1 / 29.5735, l: 33.814 };
export const toOz = (value, unit) => Math.round(Number(value) * (SIZE_UNITS[unit] || 1) * 100) / 100;

export const CONTAINER_LABEL = { rigid: "Can / bottle", flexible: "Box / bag / pouch", any: "Any container" };
export const CLASS_LABEL = { general: "Beer, soda, water, juice", wine: "Wine", spirits: "Spirits", any: "Any drink" };

export const US_STATES = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California", CO: "Colorado",
  CT: "Connecticut", DE: "Delaware", DC: "District of Columbia", FL: "Florida", GA: "Georgia", HI: "Hawaii",
  ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
  ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi",
  MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
  NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota", OH: "Ohio", OK: "Oklahoma",
  OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota",
  TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia", WA: "Washington",
  WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
};

export const sizeBand = (r) =>
  r.max_oz === null || r.max_oz === undefined
    ? Number(r.min_oz) > 0
      ? `${+r.min_oz} oz +`
      : "Any size"
    : `${Number(r.min_oz) > 0 ? `${+r.min_oz}–` : "Under "}${+r.max_oz} oz`;
